import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import videojs from 'video.js';
// The stock skin comes from the package, not a vendored copy, so skin and
// markup stay version-consistent. Our overlay is layered on top.
import 'video.js/dist/video-js.css';
// Side-effect only: registers the `seekButtons` plugin on the videojs
// prototype. Its own stylesheet (dist/videojs-seek-buttons.css) is
// deliberately NOT imported — bkfc-player.css already provides the
// skip-button icons as -webkit-mask SVGs taken byte-for-byte from the
// vendor bundle, and the plugin injects no CSS of its own at runtime
// (it only adds a `vjs-icon-replay` font-icon class, which our
// `.skip-back/.skip-forward span:before { content:"" !important; ... }`
// rules already override).
import 'videojs-seek-buttons';
// Side-effect only: registers the `vttThumbnails` plugin (scrub-bar preview
// thumbnails) on the videojs prototype. 0.0.13 is the version the vendor
// bundle shipped. Unlike seek-buttons its stylesheet IS imported: the base
// rules for the preview element are not among the extracted vendor CSS blobs
// and so are not in bkfc-player.css. See the positioning override in
// src/index.css.
import 'videojs-vtt-thumbnails';
import 'videojs-vtt-thumbnails/dist/videojs-vtt-thumbnails.css';
import './bkfc-player.css';
import { fetchPlayResponse } from '../../services/playback/fetchPlayResponse';
import { manifestFromPlayResponse } from '../../services/playback/manifestFromPlayResponse';
import { selectPlaybackMode } from '../../services/playback/selectPlaybackMode';
import { PlaybackError } from '../../services/playback/PlaybackError';
import { themeStyle } from './playerTheme';

/** Silences video.js's "using the tech directly is dangerous" warning. */
const TECH_ACCESS = { IWillNotUseThisInPlugins: true };

/**
 * PIN 6 — control bar children, verbatim from the vendor. The skip-back /
 * skip-forward buttons are NOT listed here: the `videojs-seek-buttons`
 * plugin inserts them itself via `controlBar.addChild(...)` once the player
 * is ready (see the `seekButtons()` call below), landing between
 * `volumePanel` and `currentTimeDisplay` — the vendor's rendered order is
 * playToggle, volumePanel, skip-back, skip-forward, currentTimeDisplay, ...
 */
const CONTROL_BAR_CHILDREN = [
  'playToggle',
  'volumePanel',
  'currentTimeDisplay',
  'timeDivider',
  'durationDisplay',
  'progressControl',
  'liveDisplay',
  'remainingTimeDisplay',
  'customControlSpacer',
  'fullscreenToggle',
];

/**
 * hls.js config — the house style, taken from PreviewPlayerService's 'pdp'
 * (full-quality) template. The 'thumbnail' template is not used here: this is
 * the main player, so ABR runs normally.
 */
const HLS_CONFIG = {
  enableWorker: true,
  lowLatencyMode: false,
  backBufferLength: 30,
  maxBufferLength: 60,
  maxMaxBufferLength: 120,
  startLevel: -1,
  capLevelToPlayerSize: true,
  debug: false,
  maxBufferHole: 0.5,
  manifestLoadingTimeOut: 10000,
  manifestLoadingMaxRetry: 2,
  levelLoadingTimeOut: 10000,
  levelLoadingMaxRetry: 2,
  fragLoadingTimeOut: 20000,
  fragLoadingMaxRetry: 2,
};

/**
 * BkfcPlayer — video.js chrome, hls.js engine.
 *
 * Replaces the DSP-hosted `dotplayer.js` bundle. The component owns the whole
 * pipeline: it fetches `/video/play` itself, derives the manifest, picks an
 * engine, and — PIN 7 — always ends in either playback or a visible sentence.
 * There is no silent blank area and no spinner that never clears.
 *
 * @param {object} props
 * @param {string} props.videoId - REQUIRED. The DSP oid.
 * @param {string} [props.className] - applied to the outer wrapper
 * @param {boolean} [props.autostart=true]
 * @param {boolean} [props.muted=false]
 * @param {boolean} [props.controls=true]
 * @param {string} [props.poster] - absolute URL; undefined = no poster
 * @param {boolean} [props.live] - hint only; the API response is authoritative
 * @param {number} [props.startAt] - seconds, seek-to-resume
 * @param {object} [props.theme] - overrides PLAYER_THEME
 * @param {(api: {video: HTMLVideoElement, player: object, hls: Hls|null}) => void} [props.onReady]
 * @param {() => void} [props.onEnded]
 * @param {(seconds: number) => void} [props.onTimeUpdate]
 * @param {(error: PlaybackError) => void} [props.onError]
 */
export default function BkfcPlayer({
  videoId,
  className = '',
  autostart = true,
  muted = false,
  controls = true,
  poster,
  live,
  startAt,
  theme,
  onReady,
  onEnded,
  onTimeUpdate,
  onError,
}) {
  const rootRef = useRef(null);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);

  // Everything except `videoId` is read at mount time, so it lives in a ref
  // rather than in the dependency array: a new inline `onReady` closure on each
  // render must not tear down and re-create the player.
  const latest = useRef(null);
  latest.current = {
    autostart,
    muted,
    controls,
    poster,
    live,
    startAt,
    onReady,
    onEnded,
    onTimeUpdate,
    onError,
  };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return undefined;
    }

    // React 19 StrictMode double-invokes effects. `cancelled` is the same guard
    // PreviewPlayerService.performLoad uses: every async continuation checks it
    // before touching state or the DOM, so the discarded first run goes quiet
    // instead of racing the second one.
    let cancelled = false;
    let player = null;
    let hls = null;
    let host = null;
    let nativeWatchdog = null;

    setError(null);
    setIsReady(false);

    const fail = cause => {
      const playbackError =
        cause instanceof PlaybackError
          ? cause
          : new PlaybackError('manifest_unavailable', { cause });
      // The error must be reported even after teardown started, so `onError`
      // fires regardless; only React state is skipped once unmounted.
      if (!cancelled) {
        setError(playbackError);
        setIsReady(false);
      }
      try {
        latest.current.onError?.(playbackError);
      } catch (callbackError) {
        console.error('BkfcPlayer: onError callback threw', callbackError);
      }
    };

    if (!videoId) {
      fail(new PlaybackError('not_found'));
      return undefined;
    }

    // video.js REMOVES its element from the DOM on dispose (Component.dispose ->
    // parentNode.removeChild). With `data-vjs-player` it adopts that wrapper as
    // its own element, so the wrapper and the <video> are created imperatively
    // and are not React-owned — otherwise dispose would rip a node out from
    // under React and the next StrictMode pass would mount into a detached
    // tree. React owns only `.bkfc-player`.
    host = document.createElement('div');
    host.setAttribute('data-vjs-player', '');
    const videoEl = document.createElement('video');
    videoEl.className = 'video-js dotplayer-vjs';
    videoEl.setAttribute('playsinline', '');
    host.appendChild(videoEl);
    root.appendChild(host);

    // The bridge sizes the manifest to the rendered player box. Measured off
    // the real element; fetchPlayResponse falls back to 1920x1080 when the box
    // has no layout yet (hidden, or jsdom).
    const rect = videoEl.getBoundingClientRect();

    const setup = async () => {
      let manifest;
      try {
        const body = await fetchPlayResponse(videoId, {
          width: rect.width,
          height: rect.height,
        });
        manifest = manifestFromPlayResponse(body);
      } catch (fetchError) {
        fail(fetchError);
        return;
      }
      if (cancelled) {
        return;
      }

      try {
        player = videojs(videoEl, {
          controls: latest.current.controls,
          autoplay: latest.current.autostart,
          muted: latest.current.muted,
          poster: latest.current.poster,
          // The API response is authoritative for liveness, not the `live`
          // prop. `liveui` must be an init option, which is why the fetch runs
          // before videojs().
          liveui: manifest.live,
          // hls.js is the engine. VHS must not claim the source — and we never
          // hand video.js the .m3u8 as a source on the hls.js path either.
          html5: { vhs: { overrideNative: false } },
          controlBar: { children: CONTROL_BAR_CHILDREN },
        });
      } catch (initError) {
        fail(initError);
        return;
      }

      // Vendor config, verbatim: `seekButtons({forward:10, back:10})` from
      // dotplayer.js. `forwardIndex`/`backIndex` are the plugin's own insertion
      // points into the control bar's children array (both default to 1); the
      // plugin adds `forward` first, then `back`, each via
      // `controlBar.addChild('seekButton', {...}, index)`, which splices into
      // that index. Setting both to 2 — the position of `currentTimeDisplay`
      // in CONTROL_BAR_CHILDREN — makes the forward button land there first,
      // then the back button splice in ahead of it at the same index, giving
      // the vendor's order: playToggle, volumePanel, skip-back, skip-forward,
      // currentTimeDisplay, ...
      player.seekButtons({ forward: 10, back: 10, forwardIndex: 2, backIndex: 2 });

      // Vendor config, verbatim: `t.video.trickplay_vtt_path &&
      // e.player.vttThumbnails({src: t.video.trickplay_vtt_path,
      // showTimestamp: !0})`. The guard is the vendor's and is load-bearing —
      // the plugin XHRs its `src` unconditionally, so calling it with a null
      // path (every live video) would request the page URL and log an error.
      if (manifest.trickplayUrl) {
        player.vttThumbnails({ src: manifest.trickplayUrl, showTimestamp: true });
      }

      const techEl = player.tech(TECH_ACCESS).el();

      const mode = selectPlaybackMode({
        hlsSupported: Hls.isSupported(),
        canPlayNativeHls: !!techEl.canPlayType('application/vnd.apple.mpegurl'),
      });

      if (mode === 'unsupported') {
        fail(new PlaybackError('unsupported'));
        return;
      }

      // --- ready / seek plumbing, shared by both engines ---
      let manifestParsed = false;
      let playerReady = false;
      let readyFired = false;
      let seeked = false;

      const onMediaReady = () => {
        if (cancelled) {
          return;
        }
        manifestParsed = true;

        // `startAt` seeks once. `loadedmetadata` can fire again (native src
        // changes, some Safari recoveries), so the guard is what stops a
        // re-render or a re-load from yanking the viewer back to the start.
        const resumeAt = Number(latest.current.startAt);
        if (!seeked && Number.isFinite(resumeAt) && resumeAt > 0) {
          seeked = true;
          try {
            player.currentTime(resumeAt);
          } catch (seekError) {
            console.warn('BkfcPlayer: seek to startAt failed', seekError);
          }
        }
        emitReady();
      };

      const emitReady = () => {
        if (cancelled || readyFired || !manifestParsed || !playerReady) {
          return;
        }
        readyFired = true;
        setIsReady(true);
        try {
          // PIN 4: the real media element, the video.js instance, and the
          // hls.js instance (null on native) — each one explicitly, so callers
          // stop guessing which object they hold.
          latest.current.onReady?.({ video: techEl, player, hls });
        } catch (callbackError) {
          console.error('BkfcPlayer: onReady callback threw', callbackError);
        }
      };

      player.ready(() => {
        playerReady = true;
        emitReady();
      });

      player.on('ended', () => latest.current.onEnded?.());
      player.on('timeupdate', () => latest.current.onTimeUpdate?.(player.currentTime()));

      if (mode === 'native') {
        // Safari desktop + all iOS browsers. No Hls instance is created.

        // PIN 7, the native path's only timeout. On the hls.js path a stalled
        // load eventually surfaces as a fatal NETWORK_ERROR because HLS_CONFIG
        // sets its own manifest/level/fragment timeouts. The bare media element
        // has no equivalent: Safari can accept a signed URL and then never
        // serve it, firing NEITHER `loadedmetadata` NOR `error`, which would
        // leave the spinner up forever with nothing to read. Deliberately NOT
        // added to the hls.js path, where it would double-report against
        // hls.js's own timeouts.
        //
        // 10000ms is the vendor's own DOTPLAYER_CONFIG.PLAYER_LOAD_TIMEOUT,
        // deleted from AppConfig.js along with the rest of the vendor config
        // (the value is now asserted by "breaks a stalled native load after
        // 10s instead of spinning forever" in
        // src/tests/components/player/BkfcPlayer.test.js).
        // Prior behaviour, not an invented number.
        const clearWatchdog = () => {
          clearTimeout(nativeWatchdog);
          nativeWatchdog = null;
        };
        nativeWatchdog = setTimeout(() => {
          nativeWatchdog = null;
          fail(new PlaybackError('manifest_unavailable'));
        }, 10000);

        techEl.addEventListener('loadedmetadata', () => {
          clearWatchdog();
          onMediaReady();
        });
        techEl.addEventListener('error', () => {
          clearWatchdog();
          fail(new PlaybackError('playback_failed', { cause: techEl.error }));
        });
        player.src({ src: manifest.url, type: 'application/vnd.apple.mpegurl' });
        return;
      }

      // Start loading at the resume point instead of fetching the first
      // fragment and seeking away from it; the MANIFEST_PARSED seek below stays
      // as the single source of truth for the element's position.
      const startPosition = Number(latest.current.startAt);
      hls = new Hls(
        Number.isFinite(startPosition) && startPosition > 0
          ? { ...HLS_CONFIG, startPosition }
          : HLS_CONFIG,
      );

      // Recovery is a determined/undetermined state, not a retry budget: each
      // failure class gets exactly one attempt, and a fatal error of that class
      // arriving again means recovery did not work, so the viewer is told.
      // A successfully buffered fragment proves the stream recovered, which
      // re-arms both attempts for a later, unrelated outage.
      const recovered = { network: false, media: false };

      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        recovered.network = false;
        recovered.media = false;
      });

      hls.on(Hls.Events.MANIFEST_PARSED, onMediaReady);

      hls.on(Hls.Events.ERROR, (event, data) => {
        // Safari-specific, non-fatal: nudge past the hole rather than treating
        // it as an error (PreviewPlayerService does the same).
        if (data.details === 'bufferSeekOverHole') {
          if (techEl && !Number.isNaN(techEl.duration)) {
            const seekTime = Math.min((techEl.currentTime || 0) + 0.1, techEl.duration - 0.1);
            try {
              techEl.currentTime = seekTime;
            } catch (seekError) {
              console.warn('BkfcPlayer: buffer-hole seek recovery failed', seekError);
            }
          }
          return;
        }

        if (!data.fatal) {
          return;
        }

        console.error('BkfcPlayer: fatal hls.js error', data);

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !recovered.network) {
          recovered.network = true;
          hls.startLoad();
          return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recovered.media) {
          recovered.media = true;
          hls.recoverMediaError();
          return;
        }

        fail(new PlaybackError('playback_failed', { cause: data }));
      });

      hls.loadSource(manifest.url);
      hls.attachMedia(techEl);
    };

    setup();

    // Mandatory and idempotent: safe to run twice, safe to run before setup
    // finished, safe when the player never got built.
    return () => {
      cancelled = true;
      // Cleared here too, not left to the `cancelled` guard: an unmount during
      // a stalled native load must not leave a live timer holding this closure.
      clearTimeout(nativeWatchdog);
      nativeWatchdog = null;
      try {
        hls?.destroy();
      } catch (destroyError) {
        console.warn('BkfcPlayer: hls.destroy() failed', destroyError);
      }
      hls = null;
      try {
        if (player && !player.isDisposed()) {
          player.dispose();
        }
      } catch (disposeError) {
        console.warn('BkfcPlayer: player.dispose() failed', disposeError);
      }
      player = null;
      // dispose() already removed `host` (video.js adopted it via
      // data-vjs-player). This only covers the path where it never initialised.
      try {
        host?.parentNode?.removeChild(host);
      } catch (removeError) {
        console.warn('BkfcPlayer: host cleanup failed', removeError);
      }
      host = null;
    };
  }, [videoId]);

  return (
    <div ref={rootRef} className={`bkfc-player ${className}`.trim()} style={themeStyle(theme)}>
      {error ? (
        <div className="bkfc-player-error" role="alert">
          <div className="errorMessage">{error.message}</div>
        </div>
      ) : null}
      {!error && !isReady ? (
        <div className="bkfc-player-loading" role="status" aria-label="Loading video" />
      ) : null}
    </div>
  );
}
